import React, { useEffect } from 'react';
import classnames from 'classnames';
import TimelineRecordHeader from 'domain/shared/timeline/layout/BaseTimelineRecordLayout/TimelineRecordHeader';
import TimelineRecordFooter from 'domain/shared/timeline/layout/BaseTimelineRecordLayout/TimelineRecordFooter';
import TimelineRecordActions from 'domain/shared/timeline/layout/BaseTimelineRecordLayout/TimelineRecordActions';
import { AttachmentsListField } from 'domain/shared/artifact/field';
import { useShowContext } from 'framework/show';
import { UiThemeOverrider } from 'framework/uiTheme';
import { useAuthorization } from 'framework/authorization';
import TimelineRecordFieldLayout from 'domain/shared/timeline/layout/field/TimelineRecordFieldLayout';
import { scrollToHash } from 'framework/utils';
import { HtmlField, Collapsible } from 'framework/ui/field';
import { CompositeDocumentListField } from 'domain/shared/documentStorage';
import useTimelineLayoutHelper from './timelineLayoutHelper';
import { EVALUATION_CRITERION, EVALUATION_CRITERION_ALIAS } from '../../constants';
import { Box, Typography } from '@mui/material';
import { baseTimelineEntrySx, timelineCardBodySx, timelineTextContainerSx } from './baseTimelineRecordSx';

const SCROLL_OFFSET = 100;

interface FeedbackGrade {
  description?: string;
  attachmentsIds?: string[];
}

interface FeedbackGrades {
  [key: string]: FeedbackGrade;
}

interface BaseTimelineRecordLayoutProps {
  fields?: React.ReactNode;
  actionName: string;
  actions?: React.ReactNode[];
  buttons?: React.ReactNode[];
  updateComponent?: React.ReactNode;
  isEditing?: boolean;
  isHighlighted?: boolean;
  artifactsSource?: string;
  permissionToCheck: Nullable<string | string[]>;
  basePathname?: string;
  timelineHeader?: React.ElementType;
  timelineFooter?: React.ElementType;
  timelineActions?: React.ElementType;
  timelineType?: string | ((entity: any) => string);
  [p: string]: any;
}

const BaseTimelineRecordLayout: React.FC<BaseTimelineRecordLayoutProps> = ({
  fields = null,
  actionName,
  actions = null,
  buttons = null,
  updateComponent = null,
  isEditing = false,
  isHighlighted = false,
  artifactsSource = 'attachmentsIds',
  documentsListsSource = 'artifactPaths',
  permissionToCheck,
  basePathname = '',
  timelineHeader: TimeLineHeader = TimelineRecordHeader,
  timelineFooter: TimeLineFooter = TimelineRecordFooter,
  timelineActions: TimelineActions = TimelineRecordActions,
  timelineType: initialTimelineType = 'note',
}) => {
  const { data: entity } = useShowContext();
  const subject = entity.getValueBySource('subject');
  const description = entity.getValueBySource('description');
  const feedbackGrades = (entity.getValueBySource('feedbackGrades') || {}) as FeedbackGrades;
  const timelineId = entity.getValueBySource('timelineId');
  const timelineType = typeof initialTimelineType === 'function' ? initialTimelineType(entity) : initialTimelineType;

  const { isGranted } = useAuthorization();

  const { getButtons } = useTimelineLayoutHelper();

  const attachmentsIds = entity.getValueBySource(artifactsSource);

  const entryRef = React.createRef();

  useEffect(() => {
    if (isHighlighted) {
      scrollToHash(entryRef.current, SCROLL_OFFSET);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHighlighted]);

  return permissionToCheck == null || isGranted(permissionToCheck) ? (
    <UiThemeOverrider
      overriddenLayouts={{
        BaseFieldLayout: TimelineRecordFieldLayout,
        TextFieldLayout: TimelineRecordFieldLayout,
      }}
    >
      <Box
        component="li"
        className={classnames('timeline__entry', timelineType?.toLowerCase())}
        id={`entry${timelineId}`}
        // @ts-ignore
        ref={entryRef}
        sx={baseTimelineEntrySx}
      >
        {isEditing && updateComponent ? (
          updateComponent
        ) : (
          <>
            <Box
              className="card"
              sx={
                isHighlighted
                  ? {
                      '&&&&': {
                        border: '2px solid',
                        borderColor: 'primary.main',
                      },
                    }
                  : undefined
              }
            >
              <Box className="card-body" sx={timelineCardBodySx}>
                <Box display="flex" justifyContent="space-between">
                  <TimeLineHeader actionName={actionName} timelineId={timelineId} entity={entity} />
                  <TimelineActions
                    entity={entity}
                    buttons={getButtons(buttons as React.ReactElement[])}
                    actions={actions}
                    timelineId={timelineId}
                    basePathname={basePathname}
                  />
                </Box>
                <Box sx={timelineTextContainerSx}>
                  {subject?.trim() || fields ? (
                    <Box mb="0.5rem">
                      {subject ? (
                        <Box fontSize={18} pr={5.6}>
                          <HtmlField value={subject} />
                        </Box>
                      ) : null}
                      {fields}
                    </Box>
                  ) : null}
                  {description && (
                    <Box mb="0.5rem">
                      <Collapsible>
                        <HtmlField value={description} />
                      </Collapsible>
                    </Box>
                  )}
                </Box>
                <CompositeDocumentListField
                  artifactsListSource={artifactsSource}
                  documentsListsSource={documentsListsSource}
                  EmptyComponent={null}
                />
                {feedbackGrades ? (
                  <Box>
                    {Object.entries(feedbackGrades).map(([key, value]) => {
                      const evaluationCriteria = EVALUATION_CRITERION.find(
                        item => item.value === key && item.value !== EVALUATION_CRITERION_ALIAS.NOT_ABOUT_CRITERION,
                      );
                      return value.description ? (
                        <Box key={key} mb="10px">
                          <Typography variant="body1" component="span">
                            {evaluationCriteria?.label}
                          </Typography>
                          {value.description ? <HtmlField label="" value={value.description} /> : null}
                          {value.attachmentsIds && value.attachmentsIds.length ? (
                            <AttachmentsListField value={value.attachmentsIds} />
                          ) : null}
                        </Box>
                      ) : null;
                    })}
                  </Box>
                ) : null}
                <TimeLineFooter entity={entity} noTopIndent={!attachmentsIds || !attachmentsIds.length} />
              </Box>
            </Box>
          </>
        )}
      </Box>
    </UiThemeOverrider>
  ) : null;
};

export default BaseTimelineRecordLayout;
